<script lang="ts" setup>
  import ImageVerify from '~/assets/images/verify.png'
  import GEmailVerificationForm from '~/components/relish/GEmailVerificationForm/GEmailVerificationForm.vue'
  import { useAuthStore } from '~/store'
  import type { ViewSource } from '~/types/route'

  const authStore = useAuthStore()
  const router = useIonRouter()

  const LazyAlert = defineAsyncComponent(() => import('~/components/common/GAlert/GAlert.vue'))
  const { alertContent, isAlertVisible } = useGenericAlert()

  const props = defineProps<{
    modalId: string
    source: ViewSource
    wasAutoShown: boolean
  }>()

  function onAlert(content: { header: string; message: string }) {
    isAlertVisible.value = true

    alertContent.value.header = content.header ?? 'Oops. Unexpected Error.'
    alertContent.value.message = content.message ?? 'Please check your connection or try again later.'
  }

  function onDismiss() {
    // if we are showing the skip button, track the deferred event
    if (props.wasAutoShown) {
      gaEventVerificationDeferred(props.source)
    }
    modalController.dismiss(undefined, undefined, props.modalId)
  }

  function onVerificationSuccess() {
    // If both email and mobile are verified, we can dismiss the modal
    if (props.wasAutoShown && authStore.authUser?.isEmailVerified && authStore.authUser?.isMobileVerified) {
      // give some time to ensure the previous modal is dismissed
      // before dismissing this one
      setTimeout(() => {
        modalController.dismiss(undefined, undefined, props.modalId)
      }, 500)
    }
  }

  function onNavigation() {
    router.replace({ path: '/onboard' })
  }
</script>
<template>
  <ion-page>
    <GPageHeader
      class="page-header"
      compact
      disable-box-shadow
      :hide-back-button="true"
      title="Grab your Relish Rewards!"
      background-color="var(--color-promotion-blush)"
    >
      <template #end>
        <GCloseButton :outline="false" @click="onDismiss" />
      </template>
    </GPageHeader>

    <ion-content class="ion-padding verification-content">
      <div class="verification-header">
        <div class="images-banner-wrapper">
          <div class="images-banner">
            <img :src="ImageVerify" class="image-verify" alt="Heart image" />
          </div>
        </div>
      </div>
      <div class="verification-body">
        <div class="verify-header">Verify your details</div>
        <div class="verify-info">Grab weekly coupons, receive more promotions and keep your account secure</div>
        <div class="verification-forms">
          <GEmailVerificationForm
            :verification-modal-id="modalId"
            :initial-value="authStore.authUser?.emailAddress"
            :is-verified="authStore.authUser?.isEmailVerified"
            :source="props.source"
            @verification-success="onVerificationSuccess"
            @alert="onAlert"
            @routing="onNavigation"
          />
          <GMobileVerificationForm
            :verification-modal-id="modalId"
            :initial-value="authStore.authUser?.mobileNumber"
            :is-verified="authStore.authUser?.isMobileVerified"
            :source="props.source"
            @verification-success="onVerificationSuccess"
            @alert="onAlert"
            @routing="onNavigation"
          />
        </div>
      </div>
    </ion-content>

    <LazyAlert
      :is-open="isAlertVisible"
      :header="alertContent.header"
      :message="alertContent.message"
      @will-dismiss="isAlertVisible = false"
    />
  </ion-page>
</template>
<style lang="scss" scoped>
  ion-page {
    --background: var(--color-promotion-blush);
  }

  .page-header:deep(.page-title) {
    @include typography-header-3;
  }

  .verification-content {
    --background: var(--color-promotion-blush);

    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
    justify-content: center;
    .verification-header {
      display: flex;
      padding: 0px 16px 16px;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      align-self: stretch;
      .images-banner-wrapper {
        width: 100%;
        aspect-ratio: auto;
        height: 200px;

        .images-banner {
          display: flex;
          justify-content: center;
          gap: 16px;
          .image-verify {
            max-width: 400px;
            height: 200px;
          }
        }
      }
    }
    .verification-body {
      display: flex;
      padding: 16px 0px 0px 0px;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      align-self: stretch;

      .verify-header {
        @include typography-header-5;
        align-self: stretch;
        text-align: center;
      }
      .verify-info {
        @include typography-body;
        align-self: stretch;
        color: var(--color-text-body);
        text-align: center;
      }
      .verification-forms {
        display: flex;
        flex-direction: column;
        gap: 0px;
        align-items: center;
        align-self: stretch;
        width: 100%;
      }
    }
  }
</style>
